// DailyFlowTab.tsx
import React, { useState } from "react";
import { Sun } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import { MXTextInput, MXSubmitButton } from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoDailyFlowData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Daily Flow";

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoDailyFlowData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<Sun className="w-5 h-5" />}
          title="Daily Flow"
          subtitle="Tu día, con calma"
          shareText={data.reading}
        >
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.75,
              color: colors.bodyColor,
              whiteSpace: "pre-wrap",
            }}
          >
            {data.reading}
          </p>
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Empezar Una Nueva Lectura
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<Sun className="w-5 h-5" />} title="Daily Flow">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Daily Flow
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Un vistazo tranquilo y cálido a tu día — nada saturado, solo lo esencial.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <MXTextInput value={dob} onChange={setDob} placeholder="Fecha de nacimiento (DD/MM/AAAA)" />
      </div>

      <MXSubmitButton onClick={onSubmit} disabled={!dob.trim()} label="Revelar Mi Día" />
    </>
  );
};

const DailyFlowTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Dame mi Daily Flow de hoy. Mi fecha de nacimiento: ${dob.trim()}.`);

  const data = message?.astriaMexicoData as AstriaMexicoDailyFlowData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="daily-flow">
        {loading && <MXLoadingState label="Viendo cómo fluye tu día…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="daily-flow">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaMexicoShell>
  );
};

export default DailyFlowTab;
